"use client"

import { Tag } from "@medusajs/icons"
import type { HttpTypes } from "@medusajs/types"
import { useEffect, useRef, useState } from "react"
import { useProductPurchase } from "../../context/product-purchase-context"
import { PRODUCT_COPY } from "../../lib/product-copy"
import { formatProductMoney, productDiscountPercentage } from "../../lib/product-money"
import styles from "./mobile-actions.module.css"

type MobileActionsProps = {
  product: HttpTypes.StoreProduct
  primaryActionId: string
  purchasePanelId: string
}

const STICKY_HEIGHT_VAR = "--sticky-add-to-cart-height"

const productImage = (
  product: HttpTypes.StoreProduct,
  variantId: string | null
): { src: string; alt: string } | null => {
  const variant = product.variants?.find((candidate) => candidate.id === variantId)
  const variantImage = variant?.images?.[0] as { id?: string; url?: string; alt_text?: string | null } | undefined
  if (variantImage?.url) {
    return { src: variantImage.url, alt: variantImage.alt_text?.trim() || product.title }
  }
  if (variantImage?.id) {
    const matched = product.images?.find((image) => image.id === variantImage.id)
    if (matched?.url) {
      return { src: matched.url, alt: product.title }
    }
  }
  const fallback = product.images?.[0]
  if (fallback?.url) {
    return { src: fallback.url, alt: product.title }
  }
  return product.thumbnail ? { src: product.thumbnail, alt: product.title } : null
}

export default function MobileActions({
  product,
  primaryActionId,
  purchasePanelId,
}: MobileActionsProps) {
  const purchase = useProductPurchase()
  const barRef = useRef<HTMLDivElement>(null)
  const [primaryVisible, setPrimaryVisible] = useState(true)
  const [primaryPassed, setPrimaryPassed] = useState(false)
  const [footerVisible, setFooterVisible] = useState(false)
  const [mobileViewport, setMobileViewport] = useState(false)
  const active = mobileViewport ? !primaryVisible : primaryPassed && !footerVisible

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return
    const media = window.matchMedia("(max-width: 768px)")
    const sync = () => setMobileViewport(media.matches)
    sync()
    media.addEventListener?.("change", sync)
    return () => media.removeEventListener?.("change", sync)
  }, [])

  useEffect(() => {
    const primaryAction = document.getElementById(primaryActionId)
    const footer = document.querySelector("footer")
    if (!primaryAction) return

    const primaryObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return
      setPrimaryVisible(entry.isIntersecting)
      if (entry.isIntersecting) {
        setPrimaryPassed(false)
        return
      }
      setPrimaryPassed(entry.boundingClientRect.top < 0)
    }, { threshold: 0, rootMargin: "0px" })
    primaryObserver.observe(primaryAction)

    const footerObserver = footer
      ? new IntersectionObserver(([entry]) => {
          setFooterVisible(Boolean(entry?.isIntersecting))
        }, { threshold: 0, rootMargin: "0px" })
      : null
    if (footer && footerObserver) footerObserver.observe(footer)

    return () => {
      primaryObserver.disconnect()
      footerObserver?.disconnect()
    }
  }, [primaryActionId])

  useEffect(() => {
    const root = document.documentElement
    const bar = barRef.current
    if (!active || !bar) {
      root.style.setProperty(STICKY_HEIGHT_VAR, "0px")
      return
    }

    const publishHeight = () => {
      root.style.setProperty(STICKY_HEIGHT_VAR, `${bar.offsetHeight}px`)
    }
    publishHeight()

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(publishHeight)
    observer?.observe(bar)

    return () => {
      observer?.disconnect()
      root.style.setProperty(STICKY_HEIGHT_VAR, "0px")
    }
  }, [active])

  useEffect(
    () => () => {
      document.documentElement.style.setProperty(STICKY_HEIGHT_VAR, "0px")
    },
    []
  )

  const selected = purchase.selectedPurchase
  const price = selected.display_price
  const currentPrice = price
    ? formatProductMoney(price.current_amount, price.currency_code)
    : null
  const compareAtAmount =
    price?.compare_at_amount != null && price.compare_at_amount > price.current_amount
      ? price.compare_at_amount
      : null
  const compareAt =
    price && compareAtAmount != null
      ? formatProductMoney(compareAtAmount, price.currency_code)
      : null
  const discount =
    price && compareAtAmount != null
      ? productDiscountPercentage(compareAtAmount, price.current_amount)
      : null
  const image = productImage(product, selected.variant_id)
  const adding = purchase.status === "adding"
  const incomplete = !selected.availability.complete
  const unavailable = selected.availability.complete && !selected.availability.available
  const disabled = adding || incomplete || unavailable
  const primaryLabel = adding
    ? "Ekleniyor…"
    : incomplete
      ? PRODUCT_COPY.selectOptions
      : unavailable
        ? PRODUCT_COPY.soldOut
        : PRODUCT_COPY.addToCart
  const addSelectedPurchase = () => {
    if (disabled) return
    void purchase.addCurrentSelection()
  }

  return (
    <div
      ref={barRef}
      className={`${styles.stickyAddToCart} ${active ? styles.visible : ""}`}
      data-testid="mobile-actions"
      data-visible={active ? "true" : "false"}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.productImage}>
            {image ? <img src={image.src} alt={image.alt} loading="lazy" /> : null}
          </div>

          <div className={styles.productInfo}>
            <h3 className={styles.productTitle}>{selected.option_title}</h3>
            <div className={styles.purchaseMeta}>
              <span className={styles.purchaseQuantity}>{selected.quantity} Kutu</span>
              {selected.badge_text ? (
                <span className={styles.purchaseBadge}>{selected.badge_text}</span>
              ) : null}
            </div>
            {currentPrice ? (
              <div className={styles.priceSection}>
                <span className={styles.productPrice}>{currentPrice}</span>
                {compareAt ? <span className={styles.compareAtPrice}>{compareAt}</span> : null}
                {discount != null ? (
                  <span className={styles.discountBadge}>
                    <Tag className={styles.discountIcon} aria-hidden="true" />
                    {discount}% indirim
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={styles.addToCartButton}>
            <button
              type="button"
              disabled={disabled}
              aria-label={primaryLabel}
              onClick={addSelectedPurchase}
            >
              {primaryLabel}
            </button>
          </div>
        </div>
        {purchase.status === "error" && purchase.error ? (
          <p className={styles.error} role="status">{purchase.error}</p>
        ) : null}
      </div>
    </div>
  )
}
